import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { CourtTemplatePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useSchoolScope } from "@/lib/use-school-scope";
import { usePinFilters } from "@/lib/pins-context";

// Court create form -- name + court type + optional template

export default function CourtCreatePage() {
  const navigate = useNavigate();
  const { schoolId: urlSchoolId } = useParams<{ schoolId: string }>();
  const { schools, canPickSchool } = useSchoolScope();
  const { activeSchoolIds } = usePinFilters();
  const hasPinFilter = activeSchoolIds.length > 0;

  // Resolve which schools to show in the dropdown
  const availableSchools = hasPinFilter
    ? schools.filter((s) => activeSchoolIds.includes(s.id))
    : schools;

  // School ID: URL param as initial, user can change via dropdown
  const [schoolId, setSchoolId] = useState(urlSchoolId ?? "");

  // 134 #88 -- keep the selection inside the pinned set.
  //
  // When a school is pinned, the add-facility form must default to (and stay
  // within) that pin -- the BA reported the pin being ignored here. The dropdown
  // is already scoped to `availableSchools` (pin-filtered), so whenever the
  // current selection falls outside that set (initial empty value, a URL param
  // pointing at a non-pinned school, or the active pin changing) we snap back to
  // the first pinned school. Keyed on the actual available IDs -- not just the
  // count -- so a pin SWAP that preserves the count (A -> B) still re-defaults.
  const availableIdsKey = availableSchools.map((s) => s.id).join(",");
  useEffect(() => {
    if (availableSchools.length > 0 && !availableSchools.some((s) => s.id === schoolId)) {
      setSchoolId(availableSchools[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- availableIdsKey is the stable projection of availableSchools; schoolId is intentionally excluded so the effect only corrects out-of-set selections.
  }, [availableIdsKey]);

  const [name, setName] = useState("");
  const [courtTypeId, setCourtTypeId] = useState("");
  const [courtTypes, setCourtTypes] = useState<{ id: string; name: string }[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState<CourtTemplatePublic[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load court types on mount
  useEffect(() => {
    api
      .getCourtTypes()
      .then((res) => {
        setCourtTypes(res.court_types);
        if (res.court_types.length > 0) {
          setCourtTypeId(res.court_types[0].id);
        }
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load court types",
        ),
      );
  }, []);

  const selectedTypeName = courtTypes.find((ct) => ct.id === courtTypeId)?.name ?? "";

  // Load templates when court type changes
  useEffect(() => {
    if (!courtTypeId) {
      setTemplates([]);
      setTemplateId("");
      return;
    }
    api
      .getCourtTemplates(courtTypeId)
      .then((res) => {
        setTemplates(res.templates);
        // Auto-select default template if one exists
        const defaultTemplate = res.templates.find((t) => t.is_default);
        setTemplateId(defaultTemplate ? defaultTemplate.id : "");
      })
      .catch(() => {
        setTemplates([]);
        setTemplateId("");
      });
  }, [courtTypeId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!schoolId) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.createCourt(schoolId, {
        name,
        court_type_id: courtTypeId,
        template_id: templateId || undefined,
      });
      navigate("/courts");
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to create court",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const showSchoolPicker = canPickSchool || (hasPinFilter && availableSchools.length > 1);

  return (
    <div className="flex flex-col gap-6" data-page="court-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Facility</h1>
        <p className="text-muted-foreground">
          Create a new facility for this school.
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">{selectedTypeName ? `${selectedTypeName} Details` : "Facility Details"}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="court-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            {showSchoolPicker && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="school">School</Label>
                <select
                  id="school"
                  title="School"
                  data-field="school"
                  value={schoolId}
                  onChange={(e) => setSchoolId(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  required
                >
                  {availableSchools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name ?? s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="court-type">Facility Type</Label>
              <select
                id="court-type"
                data-field="court-type"
                value={courtTypeId}
                onChange={(e) => setCourtTypeId(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                {courtTypes.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">{selectedTypeName || "Facility"} Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder={selectedTypeName ? `e.g. A ${selectedTypeName}` : "e.g. Main Field"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {templates.length > 0 && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="template">Template (optional)</Label>
                <select
                  id="template"
                  aria-label="Template"
                  data-field="template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">No template</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.is_default ? " (Default)" : ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Selecting a template will pre-configure the facility.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Creating..." : `Create ${selectedTypeName || "Facility"}`}
              </Button>
              <Link
                to="/courts"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
